import React, { useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useQuery } from '@apollo/react-hooks';
import { GET_PROVIDER_SESSIONS_BY_IDS } from 'api/graphql/v2/queries/Providers';
import { effectiveTargetSessions } from 'utils/providerCapacity/effectiveTargetSessions';
import { IProvider } from 'model/v2';
import EditIcon from '@material-ui/icons/Edit';
import { IProviderTemplateSummary } from 'model/calendar/events';
import {
  IWeeklyHoursData,
  IWeeklySessionsData
} from 'model/v2/providerCapacity';
import EditTargetSessionsModal from 'components/calendar/planner/PlannerModals/EditTargetSessionsModal';
import { NON_BOOKABLE_AVAILABILITY_LABEL } from 'utils/constants/availabilityTemplate';
import { BreakdownDay } from 'utils/mappers/response/providerTemplateSummary';
import {
  providerTemplateSummaryVar,
  staffCalendarPrefViewVar,
  views
} from 'utils/cache/calendar/index';
import { formatProviderName } from 'utils/format';

interface IProps {
  provider?: IProvider;
}

/** One row of the hours breakdown: booked from the backend, billable from the template. */
interface DayHours {
  day: string;
  booked: number;
  billable: number;
}

interface Row {
  label: string;
  booked: number;
  total: number;
  /** Hours read better as "18 / 32.5" than as whole numbers. */
  unit?: 'hours';
  /** Present when the figure can be shown day by day for checking. */
  breakdown?: {
    perDay: DayHours[];
    bookedTotal: number;
    billableTotal: number;
    excludedHours: number;
  };
  /** Weekly session limit — editable here as well as from the planner. */
  editableLimit?: boolean;
}

/** Breakdown label ('Mon') to the weekly-payload key the backend sends ('mon'). */
const WEEKLY_KEY_BY_DAY: Record<BreakdownDay, keyof IWeeklyHoursData> = {
  Mon: 'mon',
  Tue: 'tues',
  Wed: 'wed',
  Thu: 'thurs',
  Fri: 'fri',
  Sat: 'sat',
  Sun: 'sun'
};

const BREAKDOWN_ORDER: BreakdownDay[] = [
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
  'Sun'
];

/**
 * Line up the two sides of the hours breakdown.
 *
 * Booked hours come from `providerCapacity.providerBookedHours` (backend, recomputed
 * on event create / cancel / edit); billable hours come from the availability slots
 * the calendar drew. A day appears when either side has hours, so a session booked
 * outside any availability block is still visible rather than silently dropped.
 */
const mergeDayHours = (
  bookedHours: IWeeklyHoursData | undefined,
  perDay: IProviderTemplateSummary['perDay']
): DayHours[] => {
  const billableByDay = new Map(perDay.map(d => [d.day, d.availableHours]));
  return BREAKDOWN_ORDER.map(day => ({
    day,
    booked: bookedHours?.[WEEKLY_KEY_BY_DAY[day]] ?? 0,
    billable: billableByDay.get(day) ?? 0
  })).filter(d => d.booked > 0 || billableByDay.has(d.day));
};

/**
 * Shows the day-by-day arithmetic behind the weekly hours, so a scheduler who
 * wants to check the number can add up the same slots on the calendar and see
 * exactly which ones were left out.
 */
const HoursBreakdown = ({
  breakdown
}: {
  breakdown: NonNullable<Row['breakdown']>;
}) => (
  <div className="hours-breakdown">
    <table>
      <thead>
        <tr>
          <th>Day</th>
          <th>Booked</th>
          <th>Billable hrs</th>
        </tr>
      </thead>
      <tbody>
        {breakdown.perDay.map(day => (
          <tr key={day.day}>
            <td>{day.day}</td>
            <td>{day.booked}</td>
            <td>{day.billable}</td>
          </tr>
        ))}
        <tr className="total-row">
          <td>Total</td>
          <td>{breakdown.bookedTotal}</td>
          <td>{breakdown.billableTotal}</td>
        </tr>
      </tbody>
    </table>
    <p className="breakdown-note">
      Billable hours are the bookable slots on the calendar.{' '}
      {breakdown.excludedHours > 0
        ? `${breakdown.excludedHours} hrs of ${NON_BOOKABLE_AVAILABILITY_LABEL} are excluded.`
        : `${NON_BOOKABLE_AVAILABILITY_LABEL} are excluded.`}
    </p>
  </div>
);

/**
 * Same rule the planner's session badge uses: short of target reads as a
 * problem, at or over target reads as done, and no target set reads as neither.
 */
type MeterStatus = 'under' | 'met' | 'none';

const statusOf = (booked: number, total: number): MeterStatus => {
  if (total <= 0) return 'none';
  return booked >= total ? 'met' : 'under';
};

const pctOf = (booked: number, total: number) =>
  total > 0 ? Math.floor((booked / total) * 100) : 0;

/**
 * Renders the "Booked …" progress block in the provider sidebar.
 * - Week (and Month) view: weekly sessions against target, plus billable hours
 *   booked against the bookable hours in the template.
 * - Day view: "Booked Sessions" (bookedSessions / availability) and
 *   "Booked Hours" (bookedHours / capacity).
 *
 * Every bar is coloured by the same booked-vs-target rule as the planner.
 */
const BookedProgress = ({ provider }: IProps) => {
  const calendarView = useReactiveVar(staffCalendarPrefViewVar);
  const summary = useReactiveVar(providerTemplateSummaryVar);
  const capacity = provider?.providerCapacity;
  const [openBreakdown, setOpenBreakdown] = useState<string | null>(null);
  const [isEditingLimit, setIsEditingLimit] = useState(false);
  const [editedTarget, setEditedTarget] = useState<IWeeklySessionsData | null>(
    null
  );

  // Only trust the template's totals if they were published for this provider.
  const templateSummary =
    summary && Number(summary.providerId) === Number(provider?.id)
      ? summary
      : null;

  // The weekly limit can be edited here as well as from the planner, so a
  // scheduler on the provider's page does not have to go looking for it.
  // With no limit stored, the target is the care sessions on the template, the
  // same fallback the planner badge and the Ops screens use.
  const { data: sessionsData } = useQuery(GET_PROVIDER_SESSIONS_BY_IDS, {
    variables: { providerIds: [Number(provider?.id)] },
    skip: !provider?.id,
    fetchPolicy: 'cache-and-network'
  });
  const templateSessions =
    sessionsData?.getProviderSessionsByIds?.[0]?.templateSessions ?? null;
  const storedTarget = capacity?.providerTargetSessions ?? null;
  const targetSessions =
    editedTarget ??
    effectiveTargetSessions(storedTarget, templateSessions) ??
    null;

  let rows: Row[];
  if (calendarView === views.DAY) {
    rows = [
      {
        label: 'Booked Sessions',
        booked: capacity?.bookedSessions ?? 0,
        total: capacity?.availability ?? 0
      },
      {
        label: 'Booked Hours',
        booked: capacity?.bookedHours ?? 0,
        total: capacity?.capacity ?? 0
      }
    ];
  } else {
    rows = [
      {
        label: 'Booked Weekly Sessions',
        booked: capacity?.providerBookedSessions?.total ?? 0,
        total: targetSessions?.total ?? 0,
        editableLimit: true
      }
    ];
    // Billable hours booked against bookable hours in the template. The booked
    // side is the backend's providerBookedHours (recomputed on event create /
    // cancel / edit, same pass as providerBookedSessions); the billable side is
    // derived from the slots on screen. Neither is editable.
    if (templateSummary) {
      const bookedHours = capacity?.providerBookedHours;
      const perDay = mergeDayHours(bookedHours, templateSummary.perDay);
      rows.push({
        label: 'Booked Weekly Hours',
        booked: bookedHours?.total ?? 0,
        total: templateSummary.availableHours,
        unit: 'hours',
        breakdown: {
          perDay,
          bookedTotal: bookedHours?.total ?? 0,
          billableTotal: templateSummary.availableHours,
          excludedHours: templateSummary.excludedHours
        }
      });
    }
  }

  return (
    <div className="booked">
      {rows.map(row => {
        const pct = pctOf(row.booked, row.total);
        const isOpen = openBreakdown === row.label;
        return (
          <div className="booked-row" key={row.label}>
            <span className="label">
              {row.label}
              {row.editableLimit && (
                <button
                  type="button"
                  className="limit-edit"
                  aria-label="Edit weekly session limit"
                  onClick={() => setIsEditingLimit(true)}
                >
                  <EditIcon fontSize="inherit" />
                </button>
              )}
              {row.breakdown && (
                <button
                  type="button"
                  className="breakdown-toggle"
                  aria-expanded={isOpen}
                  onClick={() => setOpenBreakdown(isOpen ? null : row.label)}
                >
                  {isOpen ? 'Hide maths' : 'Show maths'}
                </button>
              )}
            </span>
            <div className="meter">
              <div className="track">
                <div
                  className={`fill ${statusOf(row.booked, row.total)}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="pct">
                {pct}% ({row.booked}/{row.total}
                {row.unit === 'hours' ? ' hrs' : ''})
              </span>
            </div>
            {row.breakdown && isOpen && (
              <HoursBreakdown breakdown={row.breakdown} />
            )}
          </div>
        );
      })}
      <EditTargetSessionsModal
        isOpen={isEditingLimit}
        onClose={() => setIsEditingLimit(false)}
        providerName={formatProviderName(provider)}
        providerId={Number(provider?.id)}
        targetSessions={targetSessions}
        onUpdate={setEditedTarget}
      />
    </div>
  );
};

export default React.memo(BookedProgress);
